// AdminApp — Admin Portal root with auth gate
// Routes under /admin/*: dashboard, users, kyc, trades, payouts, bots, content, audit, settings

import { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AdminLogin } from './AdminLogin';
import { AdminLayout } from './AdminLayout';
import { AdminDashboard } from './AdminDashboard';
import { AdminUsers } from './AdminUsers';
import { AdminUserDetail } from './AdminUserDetail';
import { AdminKyc } from './AdminKyc';
import { AdminTrades } from './AdminTrades';
import { AdminPayouts } from './AdminPayouts';
import { AdminBots } from './AdminBots';
import { AdminContent } from './AdminContent';
import { AdminAudit } from './AdminAudit';
import { AdminSettings } from './AdminSettings';
import { AdminBroadcast } from './AdminBroadcast';
import { AdminChallengesPage } from './AdminChallengesPage';
import { AdminViolationsPage } from './AdminViolationsPage';
import { AdminConsistencyPage } from './AdminConsistencyPage';
import { AdminNewsBlackoutsPage } from './AdminNewsBlackoutsPage';
import { AdminResetsPage } from './AdminResetsPage';
import { AdminCertificatesPage } from './AdminCertificatesPage';
import { AdminAutoFlattenPage } from './AdminAutoFlattenPage';
import { AdminRiskFlagsPage } from './AdminRiskFlagsPage';
import { AdminBillingPage } from './AdminBillingPage';
import { AdminMarginMonitorPage } from './AdminMarginMonitorPage';
import { AdminDiscordPage } from './AdminDiscordPage';
import { AdminTradingHallPage } from './AdminTradingHallPage';

export function AdminApp() {
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) setIsAuth(true);
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-white">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white mb-2" />
          <div className="text-[14px] text-white/60">Loading admin...</div>
        </div>
      </div>
    );
  }

  if (!isAuth) {
    return <AdminLogin onLogin={() => setIsAuth(true)} />;
  }

  return (
    <Routes>
      <Route element={<AdminLayout onLogout={() => { localStorage.removeItem('admin_token'); setIsAuth(false); }} />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="users/:id" element={<AdminUserDetail />} />
        <Route path="kyc" element={<AdminKyc />} />
        <Route path="trades" element={<AdminTrades />} />
        <Route path="payouts" element={<AdminPayouts />} />
        <Route path="bots" element={<AdminBots />} />
        <Route path="content" element={<AdminContent />} />
        <Route path="audit" element={<AdminAudit />} />
        <Route path="broadcast" element={<AdminBroadcast />} />
        <Route path="settings" element={<AdminSettings />} />
        {/* v28: marketing-aligned admin pages */}
        <Route path="challenges" element={<AdminChallengesPage />} />
        <Route path="violations" element={<AdminViolationsPage />} />
        <Route path="consistency" element={<AdminConsistencyPage />} />
        <Route path="news-blackouts" element={<AdminNewsBlackoutsPage />} />
        <Route path="resets" element={<AdminResetsPage />} />
        <Route path="certificates" element={<AdminCertificatesPage />} />
        <Route path="auto-flatten" element={<AdminAutoFlattenPage />} />
        <Route path="risk-flags" element={<AdminRiskFlagsPage />} />
        <Route path="billing" element={<AdminBillingPage />} />
        <Route path="margin" element={<AdminMarginMonitorPage />} />
        <Route path="discord" element={<AdminDiscordPage />} />
        <Route path="trading-hall" element={<AdminTradingHallPage />} />
      </Route>
    </Routes>
  );
}
